import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import * as OBC from '@thatopen/components'
import * as OBF from '@thatopen/components-front'
import * as FRAGS from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import { ultimoDireitoFoiArrasto } from '@viewer/canvas/arrastoDireito'
import { viewerApi } from '@viewer/lib/viewerApi'
import { pickWorldPoint } from '@viewer/lib/picking'
import { measureApi, setTotalCount } from './measureApi'
import type { LengthUnit } from '@viewer/store/measureSlice'

// Bootstraps the LengthMeasurement and AreaMeasurement components
// against the current world, wires them to the measure slice (mode,
// units, visibility), and tracks the live cursor-world position for
// the coordinates HUD when enabled.
//
// The hook is consumed once by Viewer3D. World/components only become
// available after useViewer's async init resolves, so we subscribe to
// viewerApi.subscribe() to trigger setup as soon as setWorld fires.
export function useMeasure(): void {
  // Track tools per world: components are tied to a specific
  // OBC.Components instance, so on a world re-init (StrictMode unmount)
  // we have to dispose and re-create.
  const lengthRef = useRef<OBF.LengthMeasurement | null>(null)
  const areaRef = useRef<OBF.AreaMeasurement | null>(null)
  const componentsRef = useRef<OBC.Components | null>(null)

  // ---- Bootstrap when world becomes available ----
  useEffect(() => {
    const tryBootstrap = () => {
      const world = viewerApi.getWorld()
      if (!world) return
      // OBC.SimpleWorld extends Base which exposes `components`:
      // every member of the world points at the same instance.
      const components = (world as unknown as { components: OBC.Components })
        .components
      if (!components) return
      if (componentsRef.current === components) return
      // World changed (or first init). Tear down whatever we had.
      teardown()

      const length = components.get(OBF.LengthMeasurement)
      const area = components.get(OBF.AreaMeasurement)
      length.world = world
      area.world = world
      length.enabled = false
      area.enabled = false
      length.visible = useAppStore.getState().measurementsVisible
      area.visible = useAppStore.getState().measurementsVisible
      const units = useAppStore.getState().measureUnits
      length.units = units
      area.units = lengthToAreaUnit(units)
      length.rounding = useAppStore.getState().measureRounding
      area.rounding = useAppStore.getState().measureRounding

      // ---- Snap classes ----
      // Default snappings are usually point-only. Enable POINT (vertex
      // endpoints), LINE (edges with midpoint/endpoint detection) and
      // FACE (planar surfaces, gives the perpendicular-to-face snap).
      const allSnaps = [
        FRAGS.SnappingClass.POINT,
        FRAGS.SnappingClass.LINE,
        FRAGS.SnappingClass.FACE,
      ]
      length.snappings = allSnaps
      area.snappings = allSnaps

      // ---- Visibility boost ----
      // Default ThatOpen styling is faint: thin grey line, tiny dot,
      // small text. Push everything to amber, depthTest off so cotas
      // never get hidden by geometry, and bump the snap marker size.
      const accent = new THREE.Color('#f59e0b')
      length.color = accent
      area.color = accent

      const lineMat = new THREE.LineBasicMaterial({
        color: accent,
        // linewidth is honoured by some platforms (ANGLE on Windows
        // ignores it and clamps to 1px) but costs nothing to set.
        linewidth: 5,
        depthTest: false,
        transparent: true,
        opacity: 1,
      })
      length.linesMaterial = lineMat
      area.linesMaterial = lineMat.clone()

      const fillMat = new THREE.MeshLambertMaterial({
        color: accent,
        transparent: true,
        opacity: 0.28,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
      area.fillsMaterial = fillMat

      // Endpoint marker: large amber ring with white core. The
      // measurement clones this element for every endpoint of every
      // dimension line.
      length.linesEndpointElement = makeEndpointElement()
      area.linesEndpointElement = makeEndpointElement()

      // Snap picker preview (the marker that moves under the cursor
      // while picking, indicating which vertex/midpoint it'll snap to).
      // Default ~6, push to 14 so it reads at a glance.
      length.pickerSize = 14
      area.pickerSize = 14

      // Style the dimension labels as they get added. The label itself
      // is a Mark wrapping a CSS2DObject whose `element` is a div we
      // can re-style. ThatOpen sets default styling at construction
      // time, so we override AFTER the line lands in `lines`.
      // CRITICAL: also clone the line material per-Hn here. Otherwise
      // every dimension shares `length.linesMaterial`, and disposing
      // one (e.g. cancelling the in-progress preview when the user
      // toggles the tool off) will dispose the shared material via
      // ThatOpen's Disposer.destroy(), silently killing every
      // committed cota at the same time.
      const styleAndIsolate = (line: OBF.DimensionLine) => {
        const el = line.label?.three?.element as HTMLDivElement | undefined
        if (el) el.classList.add('vn2r-measure-label')
        const lineEl = (line as unknown as { lineElement?: THREE.Line })
          .lineElement
        if (lineEl?.material) {
          const m = lineEl.material as THREE.LineBasicMaterial
          lineEl.material = m.clone()
        }
      }
      length.lines?.onItemAdded.add(styleAndIsolate)
      area.lines?.onItemAdded.add(styleAndIsolate)
      area.labels?.onItemAdded.add((mark: OBF.Mark) => {
        const el = mark.three?.element as HTMLDivElement | undefined
        if (!el) return
        el.classList.add('vn2r-measure-label')
      })

      // Same cure for the in-progress preview (which is NOT added to
      // `lines`, so the hook above never sees it). Wrap createLineElement
      // so every Hn (preview and committed) gets its own material clone.
      const wrapCreate = (
        tool: OBF.LengthMeasurement | OBF.AreaMeasurement,
      ) => {
        const orig = (tool as unknown as {
          createLineElement: (...args: unknown[]) => OBF.DimensionLine
        }).createLineElement.bind(tool)
        ;(tool as unknown as {
          createLineElement: (...args: unknown[]) => OBF.DimensionLine
        }).createLineElement = (...args: unknown[]) => {
          const dim = orig(...args)
          const lineEl = (dim as unknown as { lineElement?: THREE.Line })
            .lineElement
          if (lineEl?.material) {
            const m = lineEl.material as THREE.LineBasicMaterial
            lineEl.material = m.clone()
          }
          return dim
        }
      }
      wrapCreate(length)
      wrapCreate(area)

      lengthRef.current = length
      areaRef.current = area
      componentsRef.current = components
      measureApi.setLength(length)
      measureApi.setArea(area)

      // Recompute total count whenever either tool's list mutates.
      const recount = () => {
        setTotalCount((length.list?.size ?? 0) + (area.list?.size ?? 0))
      }
      length.list?.onUpdated.add(recount)
      area.list?.onUpdated.add(recount)
      recount()

      // After completing a measurement the tools stay enabled so the
      // user can keep adding. We expose Esc / right-click finishers
      // via shortcuts elsewhere; nothing else to wire here.
    }

    tryBootstrap()
    const unsubscribe = viewerApi.subscribe(tryBootstrap)
    return () => {
      unsubscribe()
      teardown()
    }

    function teardown() {
      const length = lengthRef.current
      const area = areaRef.current
      if (length) {
        length.cancelCreation()
        length.enabled = false
        // Don't dispose: the components system owns the lifecycle.
      }
      if (area) {
        area.cancelCreation()
        area.enabled = false
      }
      lengthRef.current = null
      areaRef.current = null
      componentsRef.current = null
      measureApi.setLength(null)
      measureApi.setArea(null)
      setTotalCount(0)
    }
  }, [])

  // ---- Subscribe to slice changes (mode / visibility / units) ----
  useEffect(() => {
    const apply = () => {
      const length = lengthRef.current
      const area = areaRef.current
      if (!length || !area) return
      const s = useAppStore.getState()
      const mode = s.measureMode

      // Visibility: keep all accumulated measurements drawn or not.
      length.visible = s.measurementsVisible
      area.visible = s.measurementsVisible

      // Units propagate to both tools (area derives the squared form).
      if (length.units !== s.measureUnits) length.units = s.measureUnits
      const targetAreaUnits = lengthToAreaUnit(s.measureUnits)
      if (area.units !== targetAreaUnits) area.units = targetAreaUnits
      if (length.rounding !== s.measureRounding) {
        length.rounding = s.measureRounding
      }
      if (area.rounding !== s.measureRounding) {
        area.rounding = s.measureRounding
      }

      // Snap class cycle: Tab swaps between all/point/line/face. The
      // mapped subset is pushed to both tools so the in-progress snap
      // marker reflects the cycle.
      const allSnaps = [
        FRAGS.SnappingClass.POINT,
        FRAGS.SnappingClass.LINE,
        FRAGS.SnappingClass.FACE,
      ]
      const snaps =
        s.snapClass === 'point'
          ? [FRAGS.SnappingClass.POINT]
          : s.snapClass === 'line'
            ? [FRAGS.SnappingClass.LINE]
            : s.snapClass === 'face'
              ? [FRAGS.SnappingClass.FACE]
              : allSnaps
      length.snappings = snaps
      area.snappings = snaps

      // Active mode: enable exactly one tool (or none). Note that
      // enabling the tool only turns on the snap picker preview; the
      // actual point placement happens on each canvas click via the
      // separate pointerdown listener below (it calls create() per
      // click, which is how ThatOpen Measurement records points).
      //
      // The `enabled` setter on the Measurement class internally calls
      // cancelCreation() (its own setter does so). When we go from
      // enabled=true to enabled=false, we set _enabled=false BEFORE
      // cancelCreation runs, so the inner cancelCreation early-returns.
      // That means the in-progress preview lingers, but more
      // importantly, we avoid calling cancelCreation while still
      // enabled=true, which would dispose `_temp.dimension` and (since
      // we wrapped createLineElement to clone the material) only kill
      // the preview. Without the wrap, that dispose chain destroys
      // the shared linesMaterial used by every committed cota.
      if (mode === 'length-free' || mode === 'length-edge') {
        if (area.enabled) area.enabled = false
        length.mode = mode === 'length-free' ? 'free' : 'edge'
        length.enabled = true
      } else if (
        mode === 'area-free' ||
        mode === 'area-square' ||
        mode === 'area-face'
      ) {
        if (length.enabled) length.enabled = false
        area.mode =
          mode === 'area-free'
            ? 'free'
            : mode === 'area-square'
              ? 'square'
              : 'face'
        area.enabled = true
      } else {
        if (length.enabled) length.enabled = false
        if (area.enabled) area.enabled = false
      }
    }

    apply()
    const unsubscribe = useAppStore.subscribe((state, prev) => {
      if (
        state.measureMode === prev.measureMode &&
        state.measurementsVisible === prev.measurementsVisible &&
        state.measureUnits === prev.measureUnits &&
        state.measureRounding === prev.measureRounding &&
        state.snapClass === prev.snapClass
      ) {
        return
      }
      apply()
    })
    return unsubscribe
  }, [])

  // ---- Per-click create() ----
  //
  // ThatOpen Measurement does NOT auto-handle clicks. Each call to
  // `create()` snapshots the current snap-picker target and adds it
  // as the next point of the in-progress measurement. So we listen
  // on the canvas and dispatch one create() per left-click while a
  // measure mode is active.
  //
  // For area we also wire dblclick / right-click to endCreation()
  // so the user can close the polygon without having to revisit the
  // first point exactly.
  useEffect(() => {
    let cleanup: (() => void) | null = null
    const setup = () => {
      cleanup?.()
      cleanup = null
      const world = viewerApi.getWorld()
      if (!world) return
      const dom = world.renderer?.three.domElement
      if (!dom) return

      // Reused per-click for the delete-mode raycast.
      const deleteRaycaster = new THREE.Raycaster()
      // Pad the line raycast threshold so clicks within a few pixels
      // of the line still hit. Three.Raycaster.params.Line.threshold
      // is in world units, so we scale it from pixel target via the
      // distance to the screen; 0.05 is a reasonable default for
      // typical building-scale models. Tuned empirically.
      deleteRaycaster.params.Line = { threshold: 0.05 }
      const ndc = new THREE.Vector2()

      const onClick = (e: PointerEvent) => {
        if (e.button !== 0) return
        const s = useAppStore.getState()
        // Face-pick of the work plane gets first dibs on clicks.
        if (s.workPlanePickMode) return
        const length = lengthRef.current
        const area = areaRef.current
        // Delete-by-click: clicking on a committed cota removes it.
        // We raycast directly against the visible THREE.Line bodies
        // (length cotas) and the fill meshes (area polygons), then
        // delete the parent measurement via tool.list.delete(line),
        // which fires the chained onBeforeDelete handlers and
        // disposes the entire dimension (line + endpoints + label).
        // The default tool.delete() raycasts against an invisible
        // 1x1xL bbox that misses clicks landing on the endpoint
        // markers, which is what the user was hitting before.
        if (s.measureDeleteMode && (length || area)) {
          e.preventDefault()
          e.stopPropagation()
          const rect = dom.getBoundingClientRect()
          ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
          ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
          deleteRaycaster.setFromCamera(ndc, world.camera.three)
          // Scale the threshold with model size so clicks are forgiving
          // on big models too.
          const camDist = world.camera.three.position.distanceTo(
            world.camera.controls.getTarget(new THREE.Vector3()),
          )
          deleteRaycaster.params.Line = {
            threshold: Math.max(0.05, camDist * 0.01),
          }
          let deleted = false
          if (length) {
            const lineObjs: THREE.Line[] = []
            const lineToDim = new Map<THREE.Object3D, OBF.DimensionLine>()
            for (const dim of length.lines as Iterable<OBF.DimensionLine>) {
              const lineEl = (dim as unknown as { lineElement?: THREE.Line })
                .lineElement
              if (!lineEl || !lineEl.visible) continue
              lineObjs.push(lineEl)
              lineToDim.set(lineEl, dim)
            }
            const hits = deleteRaycaster.intersectObjects(lineObjs, false)
            if (hits.length > 0) {
              const dim = lineToDim.get(hits[0]!.object)
              if (dim) {
                const innerLine = (dim as unknown as { line: THREE.Line3 }).line
                ;(length.list as unknown as {
                  delete: (line: THREE.Line3) => void
                }).delete(innerLine)
                deleted = true
              }
            }
          }
          if (!deleted && area) {
            type FillEntry = {
              three: THREE.Object3D
              area: {
                points: { add: (...pts: THREE.Vector3[]) => void }
              }
            }
            const meshObjs: THREE.Object3D[] = []
            const meshToFill = new Map<THREE.Object3D, FillEntry>()
            for (const fill of area.fills as Iterable<FillEntry>) {
              if (!fill.three || !fill.three.visible) continue
              meshObjs.push(fill.three)
              meshToFill.set(fill.three, fill)
            }
            const hits = deleteRaycaster.intersectObjects(meshObjs, true)
            if (hits.length > 0) {
              let obj: THREE.Object3D | null = hits[0]!.object
              while (obj && !meshToFill.has(obj)) obj = obj.parent
              if (obj) {
                const fill = meshToFill.get(obj)
                if (fill) {
                  ;(area.list as unknown as {
                    delete: (a: FillEntry['area']) => void
                  }).delete(fill.area)
                }
              }
            }
          }
          return
        }
        if (s.measureMode === null) return
        if (
          (s.measureMode === 'length-free' || s.measureMode === 'length-edge') &&
          length
        ) {
          void length.create()
          e.preventDefault()
          e.stopPropagation()
        } else if (
          (s.measureMode === 'area-free' ||
            s.measureMode === 'area-square' ||
            s.measureMode === 'area-face') &&
          area
        ) {
          void area.create()
          e.preventDefault()
          e.stopPropagation()
        }
      }
      const onDblClick = (e: MouseEvent) => {
        const s = useAppStore.getState()
        if (s.measureMode === null) return
        const area = areaRef.current
        if (
          area &&
          (s.measureMode === 'area-free' ||
            s.measureMode === 'area-square' ||
            s.measureMode === 'area-face')
        ) {
          area.endCreation()
          e.preventDefault()
          e.stopPropagation()
        }
      }
      const onContextMenu = (e: MouseEvent) => {
        const s = useAppStore.getState()
        if (s.measureMode === null) return
        // Um arrasto com o direito roda a câmara, e rodar para ver onde
        // pôr o ponto seguinte não pode acabar com a medição. Só um
        // clique direito acaba a área ou cancela o comprimento.
        if (ultimoDireitoFoiArrasto()) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        const area = areaRef.current
        const length = lengthRef.current
        if (
          area &&
          (s.measureMode === 'area-free' ||
            s.measureMode === 'area-square' ||
            s.measureMode === 'area-face')
        ) {
          area.endCreation()
          e.preventDefault()
          e.stopPropagation()
        } else if (
          length &&
          (s.measureMode === 'length-free' ||
            s.measureMode === 'length-edge')
        ) {
          length.cancelCreation()
          e.preventDefault()
          e.stopPropagation()
        }
      }
      // Capture phase so we run BEFORE useInteraction (which already
      // bails on measureMode but defence in depth) and before the
      // browser's default contextmenu.
      dom.addEventListener('pointerdown', onClick, { capture: true })
      dom.addEventListener('dblclick', onDblClick, { capture: true })
      dom.addEventListener('contextmenu', onContextMenu, { capture: true })
      cleanup = () => {
        dom.removeEventListener('pointerdown', onClick, { capture: true })
        dom.removeEventListener('dblclick', onDblClick, { capture: true })
        dom.removeEventListener('contextmenu', onContextMenu, {
          capture: true,
        })
      }
    }
    setup()
    const unsubscribe = viewerApi.subscribe(setup)
    return () => {
      unsubscribe()
      cleanup?.()
    }
  }, [])

  // ---- Cursor world coordinates HUD ----
  //
  // Uses Fragments' built-in raycaster (with snap when in coord place
  // mode) instead of THREE.Raycaster.intersectObjects: the latter
  // crashes against streamed Fragments geometry whose BufferAttribute
  // arrays haven't been hydrated yet ("Cannot read properties of
  // undefined (reading 'N')" out of BufferAttribute.getX).
  useEffect(() => {
    let cleanup: (() => void) | null = null
    const setup = () => {
      const world = viewerApi.getWorld()
      if (!world) return
      const dom = world.renderer?.three.domElement
      if (!dom) return
      const parent = dom.parentElement

      let scheduled = false
      let lastClientX: number | null = null
      let lastClientY: number | null = null

      const compute = async () => {
        scheduled = false
        if (lastClientX === null || lastClientY === null) return
        const s = useAppStore.getState()
        if (!s.showCoordinates) return
        // Snap when the user is in coord place mode so the cursor
        // marker locks onto the nearest vertex / edge (and the HUD
        // shows the snapped coordinates).
        const useSnap = s.annotationPlaceMode === 'coord'
        const result = await pickWorldPoint(
          world,
          dom,
          lastClientX,
          lastClientY,
          { snap: useSnap, snapClass: s.snapClass },
        ).catch(() => null)
        if (!result) {
          useAppStore.getState().setCursorWorld(null)
          return
        }
        const p = useSnap ? result.snappedPoint : result.point
        useAppStore.getState().setCursorWorld([p.x, p.y, p.z])
      }

      const schedule = () => {
        if (scheduled) return
        scheduled = true
        requestAnimationFrame(compute)
      }

      const onMove = (e: PointerEvent) => {
        lastClientX = e.clientX
        lastClientY = e.clientY
        schedule()
      }

      dom.addEventListener('pointermove', onMove)
      parent?.addEventListener('pointermove', onMove)

      // When the user toggles Coordenadas on, re-fire compute() with
      // the last known cursor position so the marker / HUD numbers
      // appear immediately, without waiting for a fresh mouse move.
      const unsubStore = useAppStore.subscribe((state, prev) => {
        if (state.showCoordinates === prev.showCoordinates) return
        if (state.showCoordinates) schedule()
      })

      cleanup = () => {
        dom.removeEventListener('pointermove', onMove)
        parent?.removeEventListener('pointermove', onMove)
        unsubStore()
      }
    }
    setup()
    const unsubscribe = viewerApi.subscribe(() => {
      cleanup?.()
      cleanup = null
      setup()
    })
    return () => {
      unsubscribe()
      cleanup?.()
    }
  }, [])

  // ---- Esc cancels in-progress measurement, then exits mode ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const s = useAppStore.getState()
      if (s.measureDeleteMode) {
        s.setMeasureDeleteMode(false)
        return
      }
      if (s.annotationPlaceMode) {
        s.setAnnotationPlaceMode(null)
        return
      }
      if (s.measureMode === null) return
      measureApi.cancelInProgress()
      s.setMeasureMode(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---- Tab cycles snap class (point → line → face → all) ----
  //
  // CAD-style behaviour: while in a measure or coord-place mode, the
  // user can press Tab to restrict the snap to a single class. This
  // helps disambiguate when several snap targets are clustered (e.g.
  // a vertex sitting at the start of an edge; Tab lets the user
  // choose which one fires).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const s = useAppStore.getState()
      const inMeasureFlow =
        s.measureMode !== null || s.annotationPlaceMode === 'coord'
      if (!inMeasureFlow) return
      // Don't let the browser tab away from the canvas while picking.
      e.preventDefault()
      e.stopPropagation()
      s.cycleSnapClass()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () =>
      window.removeEventListener('keydown', onKey, { capture: true })
  }, [])
}

function lengthToAreaUnit(u: LengthUnit): 'mm2' | 'cm2' | 'm2' | 'km2' {
  switch (u) {
    case 'mm':
      return 'mm2'
    case 'cm':
      return 'cm2'
    case 'km':
      return 'km2'
    default:
      return 'm2'
  }
}

// Builds the endpoint marker element handed to the measurement tools.
// 14px circle: amber ring + dark inner core, drop-shadow so it pops
// over both light and dark geometry.
function makeEndpointElement(): HTMLDivElement {
  const el = document.createElement('div')
  el.style.width = '14px'
  el.style.height = '14px'
  el.style.borderRadius = '50%'
  el.style.background = '#0b0b0c'
  el.style.border = '3px solid #f59e0b'
  el.style.boxShadow = '0 0 0 1px #0b0b0c, 0 0 6px rgba(245,158,11,0.7)'
  el.style.pointerEvents = 'none'
  return el
}
